<div class="bk-panel set-table">
  <data-table label="Users" [seamless]="true" [maxHeight]="'640px'" [skeletonColumns]="5" [loading]="loading">
    @if (rows.length > 0 || loading) {
      <table class="bk-log">
        <thead>
          <tr>
            <th scope="col" class="col-main">Name</th>
            <th scope="col" class="col-roles">Role</th>
            <th scope="col" class="col-status">Status</th>
            <th scope="col" class="col-added">Added</th>
            <ng-container
              *hasPermission="{
                category: 'USER_MANAGEMENT',
                anyOf: ['enable-disable-users', 'reset-user-passwords', 'delete-users']
              }"
            >
              <th scope="col" class="col-actions"><span class="bk-sr-only">Actions</span></th>
            </ng-container>
          </tr>
        </thead>
        <tbody>
          @for (row of rows; track row.user.id) {
            <tr>
              <td class="col-main">
                <span class="bk-msg">
                  <span class="bk-msg__subject">
                    {{ row.name }}
                    @if (row.isSelf) {
                      <span class="is-you">You</span>
                    }
                  </span>
                  <span class="bk-msg__preview">{{ row.email || 'No email on record' }}</span>
                </span>
              </td>

              <td class="col-roles">
                <span class="bk-msg ut__role" [matTooltip]="row.roleExtra ? row.roleAll : ''">
                  <span class="bk-msg__subject ut__role-name">
                    {{ row.roleLabel }}
                    @if (row.roleExtra) {
                      <span class="ut__more">+{{ row.roleExtra }}</span>
                      <span class="bk-sr-only">: {{ row.roleAll }}</span>
                    }
                  </span>
                  @if (row.roleDesc) {
                    <span class="bk-msg__preview">{{ row.roleDesc }}</span>
                  }
                </span>
              </td>

              <td class="col-status">
                <app-status [text]="row.statusText" [status]="row.status" />
              </td>

              <td class="quiet col-added">{{ row.added }}</td>

              <ng-container
                *hasPermission="{
                  category: 'USER_MANAGEMENT',
                  anyOf: ['enable-disable-users', 'reset-user-passwords', 'delete-users']
                }"
              >
                <td class="col-actions">
                  <span class="bk-row-actions">
                    <!-- Edit -->
                    <button
                      *hasPermission="{ category: 'USER_MANAGEMENT', permission: 'edit-users' }"
                      type="button"
                      class="bk-icon-btn"
                      [attr.aria-label]="'Edit ' + row.name"
                      matTooltip="Edit details and roles"
                      (click)="onEditClick(row.user)"
                    >
                      <span class="bk-sym" aria-hidden="true">edit</span>
                    </button>

                    <!-- Reset password, or resend the invite for people who never signed in -->
                    <button
                      *hasPermission="{ category: 'USER_MANAGEMENT', permission: 'reset-user-passwords' }"
                      type="button"
                      class="bk-icon-btn"
                      [attr.aria-label]="(row.state === 'pending' ? 'Resend invite to ' : 'Send password reset to ') + row.name"
                      [matTooltip]="row.state === 'pending' ? 'Resend invite' : 'Send password reset'"
                      (click)="onResetPasswordClick(row.user)"
                    >
                      <span class="bk-sym" aria-hidden="true">{{
                        row.state === 'pending' ? 'forward_to_inbox' : 'lock_reset'
                      }}</span>
                    </button>

                    <!-- Disable or enable, never for yourself or the only administrator.
                         One button that switches, so focus stays on it after the prompt closes. -->
                    <ng-container *hasPermission="{ category: 'USER_MANAGEMENT', permission: 'enable-disable-users' }">
                      <button
                        type="button"
                        class="bk-icon-btn"
                        [class.ut__danger]="row.user.enabled"
                        [attr.aria-disabled]="row.user.enabled && disableBlockedReason(row) ? 'true' : null"
                        [attr.aria-label]="(row.user.enabled ? 'Disable ' : 'Enable ') + row.name"
                        [attr.aria-describedby]="
                          row.user.enabled && disableBlockedReason(row) ? 'ut-why-' + row.user.id : null
                        "
                        [matTooltip]="
                          row.user.enabled ? disableBlockedReason(row) || 'Disable sign-in' : 'Enable sign-in'
                        "
                        (click)="row.user.enabled ? onDisableClick(row.user) : onEnableClick(row.user)"
                      >
                        <span class="bk-sym" aria-hidden="true">{{
                          row.user.enabled ? 'person_off' : 'person_check'
                        }}</span>
                      </button>
                      @if (row.user.enabled && disableBlockedReason(row)) {
                        <span class="bk-sr-only" [id]="'ut-why-' + row.user.id">{{ disableBlockedReason(row) }}</span>
                      }
                    </ng-container>
                  </span>
                </td>
              </ng-container>
            </tr>
          }
        </tbody>
      </table>
    } @else {
      <div class="set-empty">
        @if (loadFailed) {
          <app-empty-state
            icon="error"
            message="The team list did not load"
            hint="Check your connection, then try again."
            actionLabel="Try again"
            actionIcon="refresh"
            size="medium"
            (action)="retry.emit()"
          />
        } @else if (canCreate) {
          <app-empty-state
            [icon]="searchTerm.trim() ? 'search' : 'users'"
            [message]="emptyMessage"
            [hint]="emptyHint"
            [actionLabel]="searchTerm.trim() || filtered ? '' : 'Add user'"
            actionIcon="person_add"
            size="medium"
            (action)="addUser.emit()"
          />
        } @else {
          <app-empty-state
            [icon]="searchTerm.trim() ? 'search' : 'users'"
            [message]="emptyMessage"
            [hint]="searchTerm.trim() || filtered ? emptyHint : 'An administrator can add people to your organization.'"
            size="medium"
          />
        }
      </div>
    }
  </data-table>
</div>

<div class="set-pagination">
  <app-pagination
    [totalPages]="totalUsersPages"
    [currentPage]="usersPage"
    [totalItems]="totalUsers"
    [pageSize]="pageSize"
    [loading]="usersLoading"
    (onNext)="goToNextPage()"
    (onPrev)="goToPreviousPage()"
    (changePage)="updateUsersPage($event)"
    (pageSizeChange)="onPageSizeChange($event)"
  ></app-pagination>
</div>

<!-- Disable -->
<app-prompt-card
  [openModal]="showDisablePrompt"
  [title]="'Disable ' + selectedUserName + '?'"
  buttonText="Disable sign-in"
  tone="danger"
  impact="They cannot sign in until someone enables them again. Their messages and reports stay."
  impactIcon="person_off"
  [loading]="isDisablingUser"
  (onClose)="closeDisablePrompt()"
  (onButtonClick)="confirmDisableUser()"
>
  <p class="ut__prompt">
    <strong>{{ selectedUserName }}</strong>
    @if (selectedUser?.emailAddress) {
      ({{ selectedUser?.emailAddress }})
    }
    will lose access to Black Konnect.
  </p>
</app-prompt-card>

<!-- Enable -->
<app-prompt-card
  [openModal]="showEnablePrompt"
  [title]="'Enable ' + selectedUserName + '?'"
  buttonText="Enable sign-in"
  tone="default"
  [loading]="isEnablingUser"
  (onClose)="closeEnablePrompt()"
  (onButtonClick)="confirmEnableUser()"
>
  <p class="ut__prompt">
    <strong>{{ selectedUserName }}</strong> can sign in again with their existing password and roles.
  </p>
</app-prompt-card>

<!-- Edit -->
<app-edit-user-form
  [openModal]="showEditModal"
  [user]="editingUser"
  [isSelf]="editingIsSelf"
  [lockedRoles]="editingLockedRoles"
  (onClose)="closeEditModal()"
  (onSuccess)="displayFeedback('Changes saved.', 'success')"
/>

<!-- Reset password / resend invite -->
<app-prompt-card
  [openModal]="showResetPasswordPrompt"
  [title]="selectedIsPending ? 'Resend the invite?' : 'Send a password reset link?'"
  [buttonText]="selectedIsPending ? 'Resend invite' : 'Send reset link'"
  tone="default"
  [impact]="
    selectedIsPending
      ? 'They set their password from the link, then sign in.'
      : 'They choose a new password from the link in the email.'
  "
  impactIcon="mail"
  [loading]="isResettingPassword"
  (onClose)="closeResetPasswordPrompt()"
  (onButtonClick)="confirmResetPassword()"
>
  <p class="ut__prompt">
    We will email <strong>{{ selectedUserName }}</strong> at <strong>{{ selectedUser?.emailAddress }}</strong>
    {{ selectedIsPending ? 'a new link to finish signing up.' : 'a link to choose a new password.' }}
  </p>
</app-prompt-card>
